import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import isArchived from 'archive/isArchived';
import {TagSchemaSupportedFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {getSchemaSearchableMenuOptionDatas} from 'samples/SchemaStarsUtil';
import {Schema} from 'schemas/SchemaConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import SearchableMenu, {SearchableMenuArchivableOptionDatas} from 'ui-library/menus/SearchableMenu';
import {capitalize} from 'util/NameUtil';

import {StorableSchemaGraphQL} from '../../../graphql-fragments/inventory/schemas/__generated/StorableSchemaGraphQL';
import {AnalyticsParameterProcessTaskSchemasQuery} from './__generated/AnalyticsParameterProcessTaskSchemasQuery';
import {AnalyticsParameterTypeDropdownQuery} from './__generated/AnalyticsParameterTypeDropdownQuery';
import {AnalyticsParameterTypeDropdownStorableSchemasQuery} from './__generated/AnalyticsParameterTypeDropdownStorableSchemasQuery';
import isParameterTypeEnabled from './data/isParameterTypeEnabled';
import {ParameterConfigSetting} from './ParameterConfigSetting';
import {
  LINK_PARAMETER_TYPES,
  PARAMETER_TYPE_TO_READABLE,
  PRIMITIVE_PARAMETER_TYPES,
  PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES,
} from './ParameterConstants';
import {ParameterType, ParameterTypeAndConfigSetting} from './ParameterTypes';

type SchemaFieldSelector =
  AnalyticsParameterTypeDropdownQuery['registries'][number]['schemaFieldSelectors'][number];
type ProcessTaskSchemaGraphQL =
  AnalyticsParameterProcessTaskSchemasQuery['processTaskSchemasSupportingPlatformFeature'][number];
type TagSchema = AllTagSchemasQuery['tagSchemas'][number];

const NO_OP_TOP_LEVEL_TYPES = [ParameterType.SELECTOR];

export function getEnabledParameterTypes(): Array<ParameterType> {
  const orderedParameterTypes = PRIMITIVE_PARAMETER_TYPES.concat(LINK_PARAMETER_TYPES);
  return orderedParameterTypes.filter((parameterType) => isParameterTypeEnabled(parameterType));
}

function getDisplayParameterType(type: ParameterType): string {
  return capitalize(PARAMETER_TYPE_TO_READABLE[type]);
}

function getLinkedOptionDisplayName(
  id: string,
  options: Array<MaybeArchivableItem>,
  displayType: string
): string {
  const selectedOption = options.find((option) => option.id === id);
  if (selectedOption) {
    return getArchiveAwareDisplayName(selectedOption);
  } else {
    return `Inaccessible ${displayType}`;
  }
}

function getOptionValueForType(
  type: ParameterType,
  isMulti: boolean = false,
  settingValue: null | {id: string} = null
): ParameterConfigSetting {
  switch (type) {
    case ParameterType.BIOENTITY_LINK:
      return {tagSchema: settingValue, isMulti};
    case ParameterType.PROCESS_LINK:
    case ParameterType.PROCESS_TASK_LINK:
      return {processTaskSchema: settingValue, isMulti};
    case ParameterType.SELECTOR:
      return {schemaFieldSelector: settingValue, isMulti};
    case ParameterType.STORABLE_LINK:
      return {storableSchema: settingValue, isMulti};
    default:
      return {isMulti};
  }
}

function getSchemaSubmenu(
  // getSchemaSearchableMenuOptionDatas does not need these fields, which are missing in the gql types, so we
  // can safely omit
  schemas: Array<Omit<Schema, 'allFields' | 'fields'>>,
  type: ParameterType,
  isMulti: boolean
): [SearchableMenuArchivableOptionDatas] {
  return [
    getSchemaSearchableMenuOptionDatas(schemas, [], {
      allSchemasValue: {type, configSetting: getOptionValueForType(type, isMulti)},
      transformSchema: (schema) => ({
        type,
        configSetting: getOptionValueForType(type, isMulti, {id: schema.id}),
      }),
    }),
  ];
}

export const ANALYTICS_PARAMETER_TYPE_DROPDOWN_QUERY = gql`
  query AnalyticsParameterTypeDropdownQuery @skipAudit @engComponent(name: INSIGHTS) {
    registries {
      ...SchemaFieldSelectorsForRegistryGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelectorsForRegistry').fragment}
`;
// This query is split out from the one above because storable schemas are fetched on page load, but Apollo
// Client only serves a query from cache if the entire query is present.
export const ANALYTICS_PARAMETER_TYPE_DROPDOWN_STORABLE_SCHEMAS_QUERY = gql`
  query AnalyticsParameterTypeDropdownStorableSchemasQuery @skipAudit @engComponent(name: INSIGHTS) {
    ...AllStorableSchemasGraphQL
  }
  ${getFragmentWrapper('AllStorableSchemas').fragment}
`;

export const ANALYTICS_PARAMETER_PROCESS_TASK_SCHEMAS_QUERY = gql`
  query AnalyticsParameterProcessTaskSchemasQuery @skipAudit @engComponent(name: INSIGHTS) {
    ...AllProcessTaskSchemasForSearchGraphQL
  }
  ${getFragmentWrapper('AllProcessTaskSchemasForSearch').fragment}
`;

export interface AnalyticsParameterTypeDropdownProps {
  parameterConfig: ParameterTypeAndConfigSetting;
  onChange: (parameterConfig: ParameterTypeAndConfigSetting) => void;
  disabled: boolean;
}

export default function AnalyticsParameterTypeDropdown({
  parameterConfig,
  onChange,
  disabled,
}: AnalyticsParameterTypeDropdownProps): JSX.Element | null {
  const tagSchemasResult = useBenchlingQuery<AllTagSchemasQuery>(ALL_TAG_SCHEMAS_QUERY);
  const schemaFieldSelectorsResult = useBenchlingQuery<AnalyticsParameterTypeDropdownQuery>(
    ANALYTICS_PARAMETER_TYPE_DROPDOWN_QUERY
  );
  const storableSchemasResult = useBenchlingQuery<AnalyticsParameterTypeDropdownStorableSchemasQuery>(
    ANALYTICS_PARAMETER_TYPE_DROPDOWN_STORABLE_SCHEMAS_QUERY
  );
  const processTaskSchemasResult = useBenchlingQuery<AnalyticsParameterProcessTaskSchemasQuery>(
    ANALYTICS_PARAMETER_PROCESS_TASK_SCHEMAS_QUERY
  );
  let tagSchemas: Array<TagSchema>;
  let selectors: Array<SchemaFieldSelector>;
  let storableSchemas: Array<StorableSchemaGraphQL>;
  let processTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
  if (
    tagSchemasResult.type === QueryResultType.ERROR ||
    schemaFieldSelectorsResult.type === QueryResultType.ERROR ||
    storableSchemasResult.type === QueryResultType.ERROR ||
    processTaskSchemasResult.type === QueryResultType.ERROR
  ) {
    return null;
  }

  if (tagSchemasResult.type === QueryResultType.SUCCESS) {
    tagSchemas = tagSchemasResult.data.tagSchemas.filter(
      (schema) => schema.folderItemType !== TagSchemaSupportedFolderItemType.entry
    );
  }

  if (schemaFieldSelectorsResult.type === QueryResultType.SUCCESS) {
    selectors = schemaFieldSelectorsResult.data.registries
      .map((registry) => registry.schemaFieldSelectors)
      .flat();
  }

  if (storableSchemasResult.type === QueryResultType.SUCCESS) {
    storableSchemas = (storableSchemasResult.data.boxSchemas as Array<StorableSchemaGraphQL>).concat(
      storableSchemasResult.data.containerSchemas,
      storableSchemasResult.data.locationSchemas,
      storableSchemasResult.data.plateSchemas
    );
  }

  if (processTaskSchemasResult.type === QueryResultType.SUCCESS) {
    processTaskSchemas = processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature;
  }

  if (isLoadingParameterDisplayName(parameterConfig)) {
    return <CenteredHorizontalThrobber className={css(styles.throbber)} />;
  }

  const getDisplayParameterName = (config: ParameterTypeAndConfigSetting): string => {
    if (config.type === ParameterType.BIOENTITY_LINK && config.configSetting.tagSchema) {
      return getLinkedOptionDisplayName(config.configSetting.tagSchema.id, tagSchemas, 'entity schema');
    } else if (config.type === ParameterType.SELECTOR && config.configSetting.schemaFieldSelector) {
      return getLinkedOptionDisplayName(config.configSetting.schemaFieldSelector.id, selectors, 'dropdown');
    } else if (config.type === ParameterType.STORABLE_LINK && config.configSetting.storableSchema) {
      return getLinkedOptionDisplayName(
        config.configSetting.storableSchema.id,
        storableSchemas,
        'inventory schema'
      );
    } else if (
      (config.type === ParameterType.PROCESS_LINK && config.configSetting.processTaskSchema) ||
      (config.type === ParameterType.PROCESS_TASK_LINK && config.configSetting.processTaskSchema)
    ) {
      return getLinkedOptionDisplayName(
        config.configSetting.processTaskSchema.id,
        processTaskSchemas,
        'workflow task schema'
      );
    }
    return getDisplayParameterType(config.type);
  };

  function isLoadingParameterDisplayName(config: ParameterTypeAndConfigSetting): boolean {
    return (
      (config.type === ParameterType.BIOENTITY_LINK &&
        config.configSetting.tagSchema != null &&
        tagSchemasResult.type === QueryResultType.PENDING) ||
      (config.type === ParameterType.SELECTOR &&
        config.configSetting.schemaFieldSelector != null &&
        schemaFieldSelectorsResult.type === QueryResultType.PENDING) ||
      (config.type === ParameterType.STORABLE_LINK &&
        config.configSetting.storableSchema != null &&
        storableSchemasResult.type === QueryResultType.PENDING) ||
      ((config.type === ParameterType.PROCESS_LINK || config.type === ParameterType.PROCESS_TASK_LINK) &&
        config.configSetting.processTaskSchema != null &&
        processTaskSchemasResult.type === QueryResultType.PENDING)
    );
  }

  const getSubmenu = (type: ParameterType): Array<SearchableMenuArchivableOptionDatas> | null => {
    const loadingOption = [{name: 'Loading...', disabled: true}];

    if (type === ParameterType.BIOENTITY_LINK) {
      if (tagSchemasResult.type === QueryResultType.PENDING) {
        return [loadingOption];
      }

      return getSchemaSubmenu(
        naturalSortByKey(tagSchemas, 'name'),
        ParameterType.BIOENTITY_LINK,
        parameterConfig.configSetting.isMulti
      );
    } else if (type === ParameterType.SELECTOR) {
      if (schemaFieldSelectorsResult.type === QueryResultType.PENDING) {
        return [loadingOption];
      }

      return [
        selectors.length > 0
          ? selectors
              .filter((selector) => !isArchived(selector))
              .map((selector) => ({
                name: selector.name,
                value: {
                  type: ParameterType.SELECTOR,
                  configSetting: {schemaFieldSelector: {id: selector.id}},
                },
              }))
          : [{name: 'No dropdowns found'}],
      ];
    } else if (type === ParameterType.STORABLE_LINK) {
      if (storableSchemasResult.type === QueryResultType.PENDING) {
        return [loadingOption];
      }

      return getSchemaSubmenu(
        storableSchemas,
        ParameterType.STORABLE_LINK,
        parameterConfig.configSetting.isMulti
      );
    } else if (type === ParameterType.PROCESS_LINK || type === ParameterType.PROCESS_TASK_LINK) {
      return getSchemaSubmenu(processTaskSchemas, type, parameterConfig.configSetting.isMulti);
    }
    return null;
  };

  const menuOptions = getEnabledParameterTypes().map((type) => {
    const isMulti = parameterConfig.configSetting.isMulti && PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES[type];
    return {
      name: getDisplayParameterType(type),
      value: NO_OP_TOP_LEVEL_TYPES.includes(type)
        ? PARAMETER_TYPE_TO_READABLE[type] // Return a string for no-op types so we can show a warning if selected
        : {type, configSetting: getOptionValueForType(type, isMulti, null)},
      submenu: getSubmenu(type),
    };
  });

  const onSelect = (value: string | ParameterTypeAndConfigSetting): void => {
    if (typeof value === 'string') {
      notifications.info(`Please select a particular ${value}`);
    } else {
      onChange(value);
    }
  };

  return (
    <>
      <SelectDropdown
        buttonClassName={css(styles.dropdownButton)}
        displayName={getDisplayParameterName(parameterConfig)}
        disabled={disabled}
        buttonSize={ButtonSize.MEDIUM}
        menu={
          <SearchableMenu onSelect={onSelect} optionDatas={menuOptions} selectedValue={parameterConfig} />
        }
      />
    </>
  );
}

const styles = StyleSheet.create({
  dropdownButton: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  throbber: {
    // Using standard 10px margin on top and bottom makes the loading throbber 1px taller than the inputs
    paddingTop: 9,
    paddingBottom: 10,
  },
});
